/**
 * API · Inbox de asignaciones (rediseño v2.0).
 *
 * Una asignación está "en el inbox" del user cuando:
 *   - user_id = auth.uid()
 *   - accepted_at IS NULL
 *   - declined = false
 *   - la task asociada no está archived ni deleted
 *
 * Aceptar = SET accepted_at = now()
 * Rechazar = SET declined = true
 *
 * Cuando el user acepta, la tarea pasa a aparecer en "Mis tareas" (queries
 * que filtran assignments aceptados).
 */
import { supabase } from '@/lib/supabase';
import type { TaskAssignmentsRow, TasksRow } from '@/types/database.types';

// Acepta accepted_at y declined que vienen de la migración 20260526210000.
// Como database.types.ts se regenera al final, usamos un casting acotado.
export interface InboxAssignment extends TaskAssignmentsRow {
  accepted_at: string | null;
  declined: boolean;
  /** Task joineada para evitar N+1 al pintar el inbox. */
  task: Pick<
    TasksRow,
    | 'id'
    | 'title'
    | 'status'
    | 'priority'
    | 'scope'
    | 'area_id'
    | 'due_at'
    | 'created_by'
    | 'description'
  >;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const db = supabase as any;

export async function listMyInbox(userId: string): Promise<InboxAssignment[]> {
  // Filtramos por user_id explícito: la policy de SELECT de task_assignments es
  // a nivel tarea (is_task_participant), así que SIN este filtro el inbox traía
  // también las asignaciones de OTROS participantes de las tareas compartidas
  // (que el usuario no puede aceptar/rechazar → "no se actualizó ninguna fila").
  const { data, error } = await db
    .from('task_assignments')
    .select(
      'task_id, user_id, role, assigned_at, assigned_by, accepted_at, declined, task:tasks(id, title, status, priority, scope, area_id, due_at, created_by, description, deleted_at)',
    )
    .eq('user_id', userId)
    .is('accepted_at', null)
    .eq('declined', false)
    // Ascendente: las más viejas arriba → presión a decidir las estancadas.
    .order('assigned_at', { ascending: true });
  if (error) throw error;

  // Filtramos client-side las que tienen task null (deleted) o archived.
  const rows = (data ?? []) as InboxAssignment[];
  return rows.filter(
    (r) =>
      r.task &&
      r.task.status !== 'archived' &&
      (r.task as unknown as { deleted_at: string | null }).deleted_at === null,
  );
}

export async function acceptAssignment(taskId: string, userId: string, role: string): Promise<void> {
  // `.select()` para detectar el caso "RLS niega el UPDATE → 0 filas, sin error"
  // (pasaba antes de la policy task_assignments_update_own). Sin esto el toast
  // decía "aceptada" pero nada cambiaba.
  const { data, error } = await db
    .from('task_assignments')
    .update({ accepted_at: new Date().toISOString() })
    .match({ task_id: taskId, user_id: userId, role })
    .select('task_id');
  if (error) throw error;
  if (!data || data.length === 0) {
    throw new Error('No se actualizó ninguna fila (RLS o asignación inexistente).');
  }
}

export async function declineAssignment(
  taskId: string,
  userId: string,
  role: string,
  reason?: string | null,
): Promise<void> {
  // El motivo viaja en el MISMO update que `declined`: el trigger
  // tg_task_assignment_response lo lee de NEW.decline_reason para la notificación
  // y la entrada de timeline.
  const { data, error } = await db
    .from('task_assignments')
    .update({ declined: true, decline_reason: reason ?? null })
    .match({ task_id: taskId, user_id: userId, role })
    .select('task_id');
  if (error) throw error;
  if (!data || data.length === 0) {
    throw new Error('No se actualizó ninguna fila (RLS o asignación inexistente).');
  }
}
